import { useEffect, useState } from 'react';

interface WebsiteCarbonBadgeProps {
  className?: string;
}

interface CarbonBadgeData {
  co2PerView: number;
  cleanerThanPercent: number;
}

const TARGET_URL = 'https://pixelpro.solutions/';
const RESULT_URL = 'https://www.websitecarbon.com/website/pixelpro-solutions/';
const FALLBACK_DATA: CarbonBadgeData = {
  co2PerView: 0.06,
  cleanerThanPercent: 91,
};

function formatCo2(value: number): string {
  const rounded = Number(value.toFixed(2));
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2);
}

export default function WebsiteCarbonBadge({ className = '' }: WebsiteCarbonBadgeProps) {
  const [data, setData] = useState<CarbonBadgeData>(FALLBACK_DATA);

  useEffect(() => {
    const controller = new AbortController();

    async function loadLatestResult() {
      try {
        const response = await fetch(
          `https://api.websitecarbon.com/b?url=${encodeURIComponent(TARGET_URL)}`,
          { signal: controller.signal },
        );

        if (!response.ok) {
          return;
        }

        const payload = await response.json() as { c?: number; p?: number };
        if (typeof payload.c !== 'number' || typeof payload.p !== 'number') {
          return;
        }

        setData({
          co2PerView: payload.c,
          cleanerThanPercent: Math.round(payload.p),
        });
      } catch {
        // Keep fallback values when Website Carbon API is unavailable.
      }
    }

    void loadLatestResult();

    return () => controller.abort();
  }, []);

  return (
    <div className={className}>
      <div className="inline-block">
        <a
          href={RESULT_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View PixelPro Solutions Website Carbon results"
          className="inline-flex items-stretch text-[15px] leading-[1.15] no-underline"
        >
          <span className="inline-flex min-w-[13rem] items-center justify-center rounded-l-[0.3em] border border-r-0 border-[#00ffbc] bg-white px-3 py-1.5 text-[#0e11a8]">
            {formatCo2(data.co2PerView)}g of CO<sub className="relative top-[0.3em] text-[0.7em]">2</sub>/view
          </span>
          <span className="inline-flex items-center justify-center rounded-r-[0.3em] border border-[#00ffbc] bg-[#00ffbc] px-3 py-1.5 font-bold text-[#0e11a8]">
            Website Carbon
          </span>
        </a>
        <p className="mt-2 text-sm text-white/95">
          Cleaner than {data.cleanerThanPercent}% of pages tested
        </p>
      </div>
    </div>
  );
}
